/**
 * Notification Centre state.
 *
 * DELIBERATELY GENERAL, not approvals-specific. There are already three
 * feature-specific bells in the title bar (Security, Network, Forcepoint), each
 * with its own store and its own count, and none of them keeps any history —
 * they are live indicators that reset when you look at them. This is the other
 * thing: a durable list of what happened, server-backed, surviving a reload.
 *
 * The existing three are deliberately left alone for now. Folding them in is a
 * sensible follow-up, but ripping out three working indicators to prove a point
 * about architecture is not what this change is for.
 *
 * TWO POLLS, on purpose:
 *   - the COUNT, on a timer, for the top-bar icon. A single indexed COUNT
 *     server-side; cheap enough to run for every signed-in user.
 *   - the LIST, only when the panel is opened, plus once at startup so a new
 *     arrival can be toasted.
 * Polling the list on a timer to derive a number the count endpoint already
 * returns would drag fifty rows over the wire every 30 seconds for nothing.
 */

import { create } from 'zustand';
import * as api from '@/api/endpoints/notifications.js';

/** How often the icon refreshes its count. */
const COUNT_POLL_MS = 30_000;

export const useNotificationsStore = create((set, get) => ({
  notifications: [],
  unread: 0,
  loading: false,
  error: null,
  panelOpen: false,
  /** Ids already seen by the toast watcher, so nothing is announced twice. */
  announced: new Set(),
  /** false until the first successful list read — see maybeToast below. */
  primed: false,

  async loadCount() {
    try {
      const unread = await api.fetchUnreadCount();
      set({ unread: Number(unread) || 0 });
    } catch {
      // A failed count must not clear the badge: showing zero because a poll
      // failed would say "nothing waiting" when something may well be. Leave
      // the last known value.
    }
  },

  async loadList({ silent = false } = {}) {
    if (!silent) set({ loading: true, error: null });
    try {
      const { notifications, unread } = await api.fetchNotifications();
      const list = Array.isArray(notifications) ? notifications : [];
      set({ notifications: list, unread: Number(unread) || 0, loading: false, error: null });
      return list;
    } catch (err) {
      set({ loading: false, error: err?.message ?? 'Could not load notifications.' });
      return null;
    }
  },

  /**
   * New arrivals since the last look, for the toast watcher.
   *
   * The FIRST successful read only primes the seen-set and returns nothing.
   * Without that, opening the app would toast every unread notification at
   * once — which is exactly what the Security and Network monitors get right
   * with their silent baseline, and what this would get wrong by copying the
   * naive version.
   */
  newArrivals(list) {
    const seen = get().announced;
    const unreadOnly = list.filter((n) => !n.read);
    if (!get().primed) {
      set({ announced: new Set(list.map((n) => n.id)), primed: true });
      return [];
    }
    const fresh = unreadOnly.filter((n) => !seen.has(n.id));
    if (fresh.length > 0) {
      const next = new Set(seen);
      for (const n of list) next.add(n.id);
      set({ announced: next });
    }
    return fresh;
  },

  openPanel() {
    set({ panelOpen: true });
    void get().loadList();
  },
  closePanel() {
    set({ panelOpen: false });
  },

  async markRead(id) {
    // Optimistic: the row greys immediately and the badge drops, because the
    // round trip is not interesting to the operator.
    const before = get().notifications;
    set({
      notifications: before.map((n) => (n.id === id ? { ...n, read: true } : n)),
      unread: Math.max(0, get().unread - (before.find((n) => n.id === id)?.read ? 0 : 1))
    });
    try {
      const { unread } = await api.markNotificationRead(id);
      if (typeof unread === 'number') set({ unread });
    } catch {
      // Put it back rather than leaving the UI claiming something it did not do.
      set({ notifications: before });
      void get().loadCount();
    }
  },

  /**
   * "Ignore": take one notification off the list for good. Optimistic like
   * markRead, put back if the server refuses (a critical alert nobody has
   * claimed yet). Re-throws so the caller can show the reason.
   */
  async ignore(id) {
    const before = get().notifications;
    const gone = before.find((n) => n.id === id);
    set({
      notifications: before.filter((n) => n.id !== id),
      unread: Math.max(0, get().unread - (gone && !gone.read ? 1 : 0))
    });
    try {
      const { unread } = await api.ignoreNotification(id);
      if (typeof unread === 'number') set({ unread });
    } catch (err) {
      set({ notifications: before });
      void get().loadCount();
      throw err;
    }
  },

  async markAllRead() {
    const before = get().notifications;
    set({ notifications: before.map((n) => ({ ...n, read: true })), unread: 0 });
    try {
      await api.markAllNotificationsRead();
    } catch {
      set({ notifications: before });
      void get().loadCount();
    }
  },

  /** Start the count poll. Returns a stop function. */
  startPolling() {
    void get().loadCount();
    const timer = setInterval(() => void get().loadCount(), COUNT_POLL_MS);
    return () => clearInterval(timer);
  }
}));
